/**
 * The staff console's API client (docs/adr/0016-staff-console-and-addons.md).
 *
 * Staff identity is separate from member identity end to end: its own
 * session cookie (sent by the browser only to /api/v1/staff) and its own
 * CSRF cookie, which is the one echoed back here.
 */
import { requestWithCsrfCookie } from './api';
import type { DataEntity, LicenceOverrides, LicencePlan, OrganizationLicenceView, SubscriptionStatus } from './api';

const staffCSRFCookieName = 'commerce_ops_staff_csrf';

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestWithCsrfCookie<T>(staffCSRFCookieName, path, init);
}

function send<T>(method: string, path: string, body: unknown): Promise<T> {
  return request<T>(path, { method, body: JSON.stringify(body) });
}

export type AddonKey = 'product_distribution' | 'multi_website' | 'catalog_search';

export interface AddonInfo {
  key: AddonKey;
  label: string;
  /** What counts as evidence the organization uses this add-on. */
  in_use_means: string;
}

export interface StaffSession {
  staff: { id: string; email: string; name: string };
  addons: AddonInfo[];
}

export type SignupSource = 'self_serve' | 'checkout' | '';

export interface StaffOrganizationSummary {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  signup_source: SignupSource;
  owner_email: string;
  owner_name: string;
  subscription_status: SubscriptionStatus;
  subscription_current_period_end?: string | null;
  has_stripe_customer: boolean;
  suspended_at?: string | null;
  addons: AddonKey[];
  addons_in_use: AddonKey[];
  member_count: number;
  project_count: number;
  server_count: number;
  last_activity_at?: string | null;
  last_sign_in_at?: string | null;
}

export interface StaffOrganizationPage {
  organizations: StaffOrganizationSummary[];
  total: number;
}

export interface OrganizationAddon {
  addon: AddonKey;
  source: 'manual' | 'stripe';
  granted_at: string;
  note: string;
}

export interface StaffMember {
  user_id: string;
  email: string;
  name: string;
  role_name: string;
  is_owner: boolean;
  email_verified: boolean;
  joined_at: string;
  last_sign_in_at?: string | null;
}

export interface StaffOrganizationUsage {
  projects: number;
  environments: number;
  servers: number;
  servers_online: number;
  deployments_30_days: number;
  magento_websites: number;
  distribution_channels: { channel_type: string; total: number; enabled: number }[];
}

export type StaffAction =
  | 'staff.signed_in'
  | 'staff.sign_in_failed'
  | 'organization.addon_granted'
  | 'organization.addon_removed'
  | 'organization.suspended'
  | 'organization.reactivated'
  | 'organization.licence_set'
  | 'organization.licence_revoked'
  | 'licence_plan.saved';

/** What staff set on an organization's store licence (CO-143). */
export interface StaffLicenceInput {
  plan_code: string;
  source: 'manual' | 'stripe';
  manual_valid_until: string | null;
  grace_days: number | null;
  overrides: LicenceOverrides;
  note: string;
}

export interface StaffLicencePlanInput {
  name: string;
  entities: DataEntity[];
  production_domains: number;
  backfill_days: number;
  pii_retention_months: number;
  search_retention_months: number;
  active: boolean;
  note: string;
}

export interface StaffAuditEvent {
  id: string;
  actor: string;
  action: StaffAction;
  organization_id?: string | null;
  organization_name?: string;
  details: Record<string, unknown>;
  occurred_at: string;
}

export interface StaffOrganizationDetail {
  organization: StaffOrganizationSummary;
  suspended_reason?: string;
  suspended_by?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  addons: OrganizationAddon[];
  members: StaffMember[];
  usage: StaffOrganizationUsage;
  activity: StaffAuditEvent[];
}

export interface StaffOrganizationFilter {
  search?: string;
  subscription?: string;
  access?: string;
  addon?: string;
  limit?: number;
  offset?: number;
}

function query(filter: StaffOrganizationFilter): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== '' && value !== null) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

const organizationPath = (id: string) => `/api/v1/staff/organizations/${encodeURIComponent(id)}`;

/** The platform's operating state (RES-006). */
export interface PlatformState {
  safe_mode: boolean;
  reason?: string;
  since?: string | null;
}

export const staffApi = {
  platform: () => request<PlatformState>('/api/v1/staff/platform'),
  setSafeMode: (input: { enabled: boolean; reason?: string; confirm?: string }) =>
    send<PlatformState>('POST', '/api/v1/staff/platform/safe-mode', input),
  session: () => request<StaffSession>('/api/v1/staff/auth/session'),
  login: (email: string, password: string) => send<StaffSession>('POST', '/api/v1/staff/auth/login', { email, password }),
  logout: () => send<{ signed_out: boolean }>('POST', '/api/v1/staff/auth/logout', {}),
  listOrganizations: (filter: StaffOrganizationFilter) =>
    request<StaffOrganizationPage>(`/api/v1/staff/organizations${query(filter)}`),
  getOrganization: (id: string) => request<StaffOrganizationDetail>(organizationPath(id)),
  grantAddon: (id: string, addon: AddonKey, note: string) =>
    send<StaffOrganizationDetail>('PUT', `${organizationPath(id)}/addons/${addon}`, { note }),
  removeAddon: (id: string, addon: AddonKey, note: string) =>
    send<StaffOrganizationDetail>('DELETE', `${organizationPath(id)}/addons/${addon}`, { note }),
  suspend: (id: string, reason: string) => send<StaffOrganizationDetail>('POST', `${organizationPath(id)}/suspend`, { reason }),
  reactivate: (id: string, note: string) =>
    send<StaffOrganizationDetail>('POST', `${organizationPath(id)}/reactivate`, { note }),
  audit: (limit = 200) => request<{ events: StaffAuditEvent[] }>(`/api/v1/staff/audit?limit=${limit}`),
  licencePlans: () => request<{ plans: LicencePlan[] }>('/api/v1/staff/licence-plans'),
  saveLicencePlan: (code: string, input: StaffLicencePlanInput) =>
    send<LicencePlan>('PUT', `/api/v1/staff/licence-plans/${encodeURIComponent(code)}`, input),
  licence: (id: string) => request<OrganizationLicenceView>(`${organizationPath(id)}/licence`),
  setLicence: (id: string, input: StaffLicenceInput) => send<OrganizationLicenceView>('PUT', `${organizationPath(id)}/licence`, input),
  revokeLicence: (id: string, mode: 'grace' | 'immediate', reason: string) =>
    send<OrganizationLicenceView>('POST', `${organizationPath(id)}/licence/revoke`, { mode, reason }),
};
